import React from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../../UIElements/Modal";
import ConversationChat from "../../Chat/ConversationChat";
import { Icon } from "../../UIElements/Icon";
import { useTranslation } from "react-i18next";
import { useGlobalUtils } from "../../../../lib/globalUtils";
import {
  STATUS_LABELS,
  STATUS_BADGE_COLORS,
  getStatusGroup,
} from "@finitless/shared/orderConstants";

const ConversationAiModal = ({ order, isOpen, onClose }) => {
  const { t } = useTranslation(["conversations", "orders", "common"]);
  const navigate = useNavigate();
  const { parseDate, parsePrice } = useGlobalUtils();

  const conversationId = order?.conversation_id;
  const customerName = [order?.customer?.name, order?.customer?.lastname]
    .filter(Boolean)
    .join(" ")
    .trim();
  const customerContact = [
    order?.customer?.country_phone_code
      ? `+${order?.customer?.country_phone_code}`
      : "",
    order?.customer?.cellphone,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  const statusGroup = getStatusGroup(order?.status);
  const fallbackStatusLabel =
    STATUS_LABELS[order?.status] || order?.status || t("common:notAvailable");
  const statusLabel = t(`orders:statusLabels.${order?.status}`, {
    defaultValue: fallbackStatusLabel,
  });
  const total = order?.summary?.total;
  const formattedTotal =
    total === null || total === undefined
      ? t("common:notAvailable")
      : parsePrice(total);
  const formattedOrderDate = order?.created_at
    ? parseDate(order.created_at, { outputFormat: "MMM d, h:mm a" })
    : t("common:notAvailable");

  const contextItems = [
    {
      label: t("aiModal.context.orderNumber"),
      value:
        order?.id === null || order?.id === undefined
          ? t("common:notAvailable")
          : `#${order.id}`,
      icon: "ShoppingBag",
    },
    {
      label: t("aiModal.context.customer"),
      value: customerName || t("aiModal.context.unknownCustomer"),
      icon: "User",
      secondary: customerContact,
    },
    {
      label: t("aiModal.context.status"),
      value: statusLabel,
      icon: "CircleDot",
      statusClass:
        STATUS_BADGE_COLORS[statusGroup] || STATUS_BADGE_COLORS.unknown,
    },
    {
      label: t("aiModal.context.total"),
      value: formattedTotal,
      icon: "DollarSign",
      secondary: formattedOrderDate,
      secondaryPrefix: t("aiModal.context.orderTime"),
    },
  ];

  const handleOpenConversationDetails = () => {
    if (!conversationId) return;
    onClose();
    navigate(`/analyze/conversations/${conversationId}`);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("aiModal.title")}
      subtitle={t("aiModal.subtitle", { id: conversationId || "" })}
      maxWidth="max-w-5xl"
    >
      <div className="space-y-5">
        <div className="rounded-xl border border-gray-700 bg-gray-800/70 p-4">
          <div className="mb-3 flex items-center gap-2">
            <Icon name="Info" className="h-4 w-4 text-blue-400" />
            <p className="text-sm font-medium text-gray-200">
              {t("aiModal.context.title")}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {contextItems.map((item) => (
              <div
                key={item.label}
                className="rounded-lg border border-gray-700 bg-gray-900/70 p-3"
              >
                <div className="mb-1 flex items-center gap-2 text-xs text-gray-400">
                  <Icon name={item.icon} className="h-3.5 w-3.5" />
                  <span>{item.label}</span>
                </div>

                {item.statusClass ? (
                  <span
                    className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${item.statusClass}`}
                  >
                    {item.value}
                  </span>
                ) : (
                  <p className="truncate text-sm font-semibold text-white">
                    {item.value}
                  </p>
                )}

                {item.secondary && (
                  <p className="mt-1 text-xs text-gray-400">
                    {item.secondaryPrefix ? `${item.secondaryPrefix}: ` : ""}
                    {item.secondary}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        <ConversationChat
          conversationId={conversationId}
          maxHeight="h-[56vh]"
          showScrollButton={true}
          enablePagination={true}
          className="border-gray-700 bg-gray-800/50 p-2"
        />
      </div>

      {/* Footer */}
      <div className="mt-6 flex flex-col gap-2 border-t border-gray-800 pt-6 sm:flex-row sm:justify-end">
        <button
          onClick={handleOpenConversationDetails}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-600 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-100 transition-colors hover:border-gray-500 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!conversationId}
        >
          <Icon name="ExternalLink" className="h-4 w-4" />
          {t("aiModal.actions.openDetails")}
        </button>
        <button
          onClick={onClose}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
        >
          {t("aiModal.close")}
        </button>
      </div>
    </Modal>
  );
};

export default ConversationAiModal;
